'use client';

import { tv } from 'tailwind-variants';
import { faStar } from '@fortawesome/pro-solid-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import { EntityRatingCommentCount, Rating } from '@medprev-web-app/api-graphql-client';

import { Icon } from '@/components/ui/icon.component';
import { formatRatingAverageLabel } from '@/utils/formatters/fomat-rating-average-label';
import { AnalyticsEnumKeyType } from '@/types/enums/analytics/analytics.enum';

interface EntityRatingProps {
  rating: Rating;
  commentsCount?: EntityRatingCommentCount | null;
  ['data-analytics-event']?: AnalyticsEnumKeyType;
}

const formatRating = (ratingEntity: Rating): string => {
  const rating = ratingEntity.fiveStars.average;

  if (rating === 0) {
    return 'Sem avaliações';
  }

  const ratingNumber = Number(rating);

  return ratingNumber.toFixed(1).replace('.', ',');
};

const formatCommentCount = (commentCount: EntityRatingCommentCount): string => {
  const count = commentCount.total;

  if (count === 1) {
    return `${count} comentário`;
  }

  return `${count} comentários`;
};

const formatRatingCount = (ratingEntity: Rating): string => {
  const rating = ratingEntity.fiveStars.count;

  if (rating === 1) {
    return `${rating} avaliação`;
  }

  return `${rating} avaliações`;
};

function RatingAverage({ rating }: { rating: Rating }) {
  const { average } = rating.fiveStars;

  const averageNumber = Number(average);

  if (averageNumber === 0) {
    return <span className="text-gray-600 font-semibold text-xl mt-0.5">{formatRating(rating)}</span>;
  }

  if (averageNumber < 2.9) {
    return <span className="text-gray-600 font-semibold text-xl">{formatRating(rating)}</span>;
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-gray-600 font-semibold text-xl">
        {formatRating(rating)}
        {' '}
      </span>
      <div className="w-1 h-1 rounded-full bg-gray-600" data-testid="excellent-rating--entity-rating" />
      {' '}
      <span data-testid={DataTestIdEnum.ENTITY_AVERAGE_RATING} className="text-gray-600 font-semibold text-xl">
        {formatRatingAverageLabel(averageNumber)}
      </span>
    </div>
  );
}

function RatingCount({ rating, commentCount }: { rating: Rating; commentCount?: EntityRatingCommentCount | null }) {
  if (!commentCount) {
    return null;
  }
  const countRatting = rating.fiveStars.count || 0;
  const countComments = commentCount.total || 0;

  if (countRatting === 0 && countComments === 0) {
    return null;
  }

  if (countRatting > 0 && countComments === 0) {
    return (
      <span className="text-green-500" data-testid="rating-count--entity-rating">
        {formatRatingCount(rating)}
      </span>
    );
  }

  if (countRatting === 0 && countComments > 0) {
    return <span className="text-green-500">{formatCommentCount(commentCount)}</span>;
  }

  return (
    <span className="text-green-500" data-testid={DataTestIdEnum.ENTITY_FULL_COMMENT_COUNT}>
      {formatRatingCount(rating)}
      {' '}
      e
      {' '}
      {formatCommentCount(commentCount)}
    </span>
  );
}

const starStyle = tv({
  slots: {
    star: 'text-warning-400 -mt-2',
  },

  variants: {
    withoutRating: {
      true: {
        star: 'text-gray-400',
      },
    },
  },
});

export function EntityRating({ rating, commentsCount, 'data-analytics-event': analyticsEventName }: EntityRatingProps) {
  const { star } = starStyle();

  function goToRating() {
    const element = document.querySelector('#rating') as HTMLElement;
    const offset = 80;

    if (!element) {
      return;
    }

    window.scrollTo({
      top: element.offsetTop - offset,
      behavior: 'smooth',
    });
  }

  const doNotHaveRateEnough = rating?.fiveStars?.count === 0 || (rating?.fiveStars?.count || 0) < 3;

  if (doNotHaveRateEnough) {
    return null;
  }

  return (
    <div
      className="py-4 border border-green-200 rounded-xl flex items-center justify-center"
      data-testid={DataTestIdEnum.ENTITY_RATING_CONTAINER}
    >
      <button
        className="text-center"
        data-analytics-event={analyticsEventName}
        onClick={goToRating}
      >
        <div className="flex items-center gap-2 justify-center">
          <Icon icon={faStar} className={star({ withoutRating: rating?.fiveStars?.count === 0 })} />
          <RatingAverage rating={rating as Rating} />
        </div>

        <RatingCount rating={rating} commentCount={commentsCount} />
      </button>
    </div>
  );
}
